import { useMutation, useQuery } from '@apollo/client/react/index.js';
import {
  AnimatedCounter,
  GlassBadge,
  GlassButton,
  GlassCard,
  GlassCardContent,
  GlassCardHeader,
  GlassConfirmDialog,
  GlassEmptyState,
  GlassSelect,
  GlassTable,
  GlassTableBody,
  GlassTableCell,
  GlassTableHead,
  GlassTableHeaderCell,
  GlassTableRow,
  SkeletonLoader,
} from '@merritrac/shared/components';
import {
  AlertTriangle,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  ClipboardCheck,
  Clock,
  GitMerge,
  Inbox,
  Mail,
  MapPin,
  Phone,
  XCircle,
} from 'lucide-react';
import { useState } from 'react';

import { GET_SERVICE_REQUESTS, UPDATE_SERVICE_REQUEST_STATUS } from '../lib/graphql/operations.js';

import type { ReactElement } from 'react';

/* ── GraphQL response types ── */

interface ContactRef {
  id: number;
  firstName: string;
  lastName: string;
  email: string | null;
  phone: string | null;
}

interface PropertyRef {
  id: number;
  name: string;
  address: string | null;
  city: string | null;
  state: string | null;
}

interface ServiceRequestNode {
  id: number;
  clientCode: string;
  propertyId: number | null;
  unitAk: number | null;
  contactId: number | null;
  description: string;
  urgency: string;
  status: string;
  serviceOrderAk: number | null;
  createdAt: string;
  updatedAt: string;
  contact: ContactRef | null;
  property: PropertyRef | null;
}

interface ServiceRequestEdge {
  node: ServiceRequestNode;
}

interface ServiceRequestConnection {
  totalCount: number;
  edges: ServiceRequestEdge[];
}

interface GetServiceRequestsData {
  serviceRequests: ServiceRequestConnection;
}

/* ── Constants ── */

type StatusFilter = 'all' | 'pending' | 'approved' | 'converted' | 'rejected';

const statusFilterOptions: { value: StatusFilter; label: string }[] = [
  { value: 'all', label: 'All Statuses' },
  { value: 'pending', label: 'Pending' },
  { value: 'approved', label: 'Approved' },
  { value: 'converted', label: 'Converted' },
  { value: 'rejected', label: 'Rejected' },
];

function urgencyBadgeVariant(urgency: string): 'danger' | 'info' | 'default' {
  switch (urgency) {
    case 'urgent':
      return 'danger';
    case 'normal':
      return 'info';
    default:
      return 'default';
  }
}

function statusBadgeVariant(status: string): 'warning' | 'info' | 'success' | 'danger' | 'default' {
  switch (status) {
    case 'pending':
      return 'warning';
    case 'approved':
      return 'info';
    case 'converted':
      return 'success';
    case 'rejected':
      return 'danger';
    default:
      return 'default';
  }
}

function capitalize(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  } catch {
    return iso;
  }
}

function contactFullName(contact: ContactRef | null): string {
  if (contact === null) {
    return '—';
  }
  return [contact.firstName, contact.lastName].filter(Boolean).join(' ');
}

/* ── Expanded detail panel ── */

function RequestDetailPanel({ request }: { request: ServiceRequestNode }): ReactElement {
  return (
    <div className="grid grid-cols-1 gap-4 border-t border-glass-border bg-glass-subtle px-5 py-4 sm:grid-cols-2 lg:grid-cols-3">
      {/* Description */}
      <div className="sm:col-span-2 lg:col-span-3">
        <p className="mb-1 text-xs font-semibold uppercase tracking-wider text-text-tertiary">
          Description
        </p>
        <p className="text-sm text-text">{request.description}</p>
      </div>

      {/* Property info */}
      {request.property !== null && (
        <div>
          <p className="mb-1 text-xs font-semibold uppercase tracking-wider text-text-tertiary">
            Property
          </p>
          <p className="text-sm font-medium text-text">{request.property.name}</p>
          {(request.property.address !== null ||
            request.property.city !== null ||
            request.property.state !== null) && (
            <p className="mt-0.5 flex items-center gap-1 text-xs text-text-secondary">
              <MapPin size={11} className="shrink-0" />
              {[request.property.address, request.property.city, request.property.state]
                .filter(Boolean)
                .join(', ')}
            </p>
          )}
        </div>
      )}

      {/* Contact info */}
      {request.contact !== null && (
        <div>
          <p className="mb-1 text-xs font-semibold uppercase tracking-wider text-text-tertiary">
            Submitted By
          </p>
          <p className="text-sm font-medium text-text">{contactFullName(request.contact)}</p>
          {request.contact.email !== null && (
            <p className="mt-0.5 flex items-center gap-1 text-xs text-text-secondary">
              <Mail size={11} className="shrink-0" />
              {request.contact.email}
            </p>
          )}
          {request.contact.phone !== null && (
            <p className="mt-0.5 flex items-center gap-1 text-xs text-text-secondary">
              <Phone size={11} className="shrink-0" />
              {request.contact.phone}
            </p>
          )}
        </div>
      )}

      {/* Dates */}
      <div>
        <p className="mb-1 text-xs font-semibold uppercase tracking-wider text-text-tertiary">
          Timeline
        </p>
        <p className="text-xs text-text-secondary">
          <span className="text-text-tertiary">Submitted:</span> {formatDate(request.createdAt)}
        </p>
        <p className="text-xs text-text-secondary">
          <span className="text-text-tertiary">Updated:</span> {formatDate(request.updatedAt)}
        </p>
        {request.serviceOrderAk !== null && (
          <p className="mt-0.5 text-xs text-success">
            Work Order #{String(request.serviceOrderAk)}
          </p>
        )}
      </div>
    </div>
  );
}

/* ── Reject dialog ── */

function RejectDialog({
  request,
  open,
  onClose,
  onRejected,
}: {
  request: ServiceRequestNode | null;
  open: boolean;
  onClose: () => void;
  onRejected: () => void;
}): ReactElement {
  const [updateStatus, { loading }] = useMutation(UPDATE_SERVICE_REQUEST_STATUS, {
    refetchQueries: [{ query: GET_SERVICE_REQUESTS, variables: { first: 200 } }],
  });

  return (
    <GlassConfirmDialog
      open={open}
      title="Reject Request"
      message={
        request !== null
          ? `Are you sure you want to reject request #${String(request.id)}? This will notify the submitter that their request has been declined.`
          : ''
      }
      confirmLabel="Reject Request"
      variant="danger"
      loading={loading}
      onCancel={onClose}
      onConfirm={() => {
        if (request === null) {
          return;
        }
        void updateStatus({ variables: { id: request.id, status: 'rejected' } }).then(() => {
          onRejected();
        });
      }}
    />
  );
}

/* ── Page ── */

export function ServiceRequestsPage(): ReactElement {
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [rejectTarget, setRejectTarget] = useState<ServiceRequestNode | null>(null);

  const { data, loading, error } = useQuery<GetServiceRequestsData>(GET_SERVICE_REQUESTS, {
    variables: {
      first: 200,
      ...(statusFilter !== 'all' ? { status: statusFilter } : {}),
    },
  });

  const [updateStatus, { loading: converting }] = useMutation(UPDATE_SERVICE_REQUEST_STATUS, {
    refetchQueries: [
      { query: GET_SERVICE_REQUESTS, variables: { first: 200 } },
      {
        query: GET_SERVICE_REQUESTS,
        variables: {
          first: 200,
          ...(statusFilter !== 'all' ? { status: statusFilter } : {}),
        },
      },
    ],
  });

  const requests: ServiceRequestNode[] = data?.serviceRequests.edges.map((e) => e.node) ?? [];

  const totalCount = data?.serviceRequests.totalCount ?? requests.length;
  const pendingCount = requests.filter((r) => r.status === 'pending').length;
  const approvedCount = requests.filter((r) => r.status === 'approved').length;
  const convertedCount = requests.filter((r) => r.status === 'converted').length;

  function handleConvert(request: ServiceRequestNode): void {
    void updateStatus({ variables: { id: request.id, status: 'converted' } });
  }

  function toggleExpand(id: number): void {
    setExpandedId((prev) => (prev === id ? null : id));
  }

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="flex items-center justify-between">
        <p className="text-sm text-text-secondary">
          Review and triage service requests submitted by unit owners
        </p>
      </div>

      {/* Stats row */}
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
        <GlassCard>
          <GlassCardContent className="flex items-center gap-3 py-4">
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary-soft">
              <Inbox size={18} className="text-primary" />
            </div>
            <div className="min-w-0">
              <p className="text-xl font-semibold text-text leading-none">
                {loading ? (
                  <SkeletonLoader className="h-6 w-10" />
                ) : (
                  <AnimatedCounter value={totalCount} />
                )}
              </p>
              <p className="mt-0.5 truncate text-xs text-text-secondary">Total</p>
            </div>
          </GlassCardContent>
        </GlassCard>

        <GlassCard>
          <GlassCardContent className="flex items-center gap-3 py-4">
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-warning-soft">
              <Clock size={18} className="text-warning" />
            </div>
            <div className="min-w-0">
              <p className="text-xl font-semibold text-text leading-none">
                {loading ? (
                  <SkeletonLoader className="h-6 w-10" />
                ) : (
                  <AnimatedCounter value={pendingCount} />
                )}
              </p>
              <p className="mt-0.5 truncate text-xs text-text-secondary">Pending</p>
            </div>
          </GlassCardContent>
        </GlassCard>

        <GlassCard>
          <GlassCardContent className="flex items-center gap-3 py-4">
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-info-soft">
              <ClipboardCheck size={18} className="text-info" />
            </div>
            <div className="min-w-0">
              <p className="text-xl font-semibold text-text leading-none">
                {loading ? (
                  <SkeletonLoader className="h-6 w-10" />
                ) : (
                  <AnimatedCounter value={approvedCount} />
                )}
              </p>
              <p className="mt-0.5 truncate text-xs text-text-secondary">Approved</p>
            </div>
          </GlassCardContent>
        </GlassCard>

        <GlassCard>
          <GlassCardContent className="flex items-center gap-3 py-4">
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-success-soft">
              <CheckCircle2 size={18} className="text-success" />
            </div>
            <div className="min-w-0">
              <p className="text-xl font-semibold text-text leading-none">
                {loading ? (
                  <SkeletonLoader className="h-6 w-10" />
                ) : (
                  <AnimatedCounter value={convertedCount} />
                )}
              </p>
              <p className="mt-0.5 truncate text-xs text-text-secondary">Converted</p>
            </div>
          </GlassCardContent>
        </GlassCard>
      </div>

      {/* Table card */}
      <GlassCard>
        <GlassCardHeader>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <h2 className="text-base font-semibold text-text">Service Requests</h2>
            <GlassSelect
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value as StatusFilter);
              }}
              options={statusFilterOptions}
              className="w-full sm:w-44"
              aria-label="Filter by status"
            />
          </div>
        </GlassCardHeader>

        {loading ? (
          <div className="space-y-3 p-5">
            <SkeletonLoader className="h-10 w-full" />
            <SkeletonLoader className="h-10 w-full" />
            <SkeletonLoader className="h-10 w-full" />
          </div>
        ) : error !== undefined ? (
          <div className="py-12 text-center text-sm text-danger">
            Failed to load service requests: {error.message}
          </div>
        ) : requests.length === 0 ? (
          <div className="py-8">
            <GlassEmptyState
              icon={Inbox}
              title="No requests found"
              description={
                statusFilter !== 'all'
                  ? 'No requests match the selected status filter.'
                  : 'There are no service requests yet.'
              }
            />
          </div>
        ) : (
          <div className="overflow-x-auto">
            <GlassTable>
              <GlassTableHead>
                <GlassTableRow>
                  <GlassTableHeaderCell className="w-8" aria-label="Expand" />
                  <GlassTableHeaderCell>Request #</GlassTableHeaderCell>
                  <GlassTableHeaderCell className="hidden lg:table-cell">
                    Description
                  </GlassTableHeaderCell>
                  <GlassTableHeaderCell>Urgency</GlassTableHeaderCell>
                  <GlassTableHeaderCell>Status</GlassTableHeaderCell>
                  <GlassTableHeaderCell className="hidden md:table-cell">
                    Submitter
                  </GlassTableHeaderCell>
                  <GlassTableHeaderCell className="hidden xl:table-cell">
                    Property
                  </GlassTableHeaderCell>
                  <GlassTableHeaderCell className="hidden sm:table-cell">
                    Submitted
                  </GlassTableHeaderCell>
                  <GlassTableHeaderCell aria-label="Actions" />
                </GlassTableRow>
              </GlassTableHead>
              <GlassTableBody>
                {requests.map((request) => {
                  const isExpanded = expandedId === request.id;
                  const isConverted = request.status === 'converted';
                  const isRejected = request.status === 'rejected';
                  const canAct = !isConverted && !isRejected;

                  return (
                    <>
                      <GlassTableRow
                        key={request.id}
                        className="cursor-pointer"
                        onClick={() => {
                          toggleExpand(request.id);
                        }}
                      >
                        {/* Expand toggle */}
                        <GlassTableCell>
                          <span className="text-text-tertiary">
                            {isExpanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                          </span>
                        </GlassTableCell>

                        {/* Request # */}
                        <GlassTableCell>
                          <span className="font-mono text-sm font-medium text-text">
                            #{String(request.id)}
                          </span>
                        </GlassTableCell>

                        {/* Description (truncated) */}
                        <GlassTableCell className="hidden lg:table-cell">
                          <span className="block max-w-[260px] truncate text-sm text-text-secondary">
                            {request.description}
                          </span>
                        </GlassTableCell>

                        {/* Urgency */}
                        <GlassTableCell>
                          <GlassBadge variant={urgencyBadgeVariant(request.urgency)}>
                            {capitalize(request.urgency)}
                          </GlassBadge>
                        </GlassTableCell>

                        {/* Status */}
                        <GlassTableCell>
                          <GlassBadge variant={statusBadgeVariant(request.status)}>
                            {capitalize(request.status)}
                          </GlassBadge>
                        </GlassTableCell>

                        {/* Submitter */}
                        <GlassTableCell className="hidden md:table-cell text-text-secondary">
                          {contactFullName(request.contact)}
                        </GlassTableCell>

                        {/* Property */}
                        <GlassTableCell className="hidden xl:table-cell text-text-secondary">
                          {request.property?.name ?? '—'}
                        </GlassTableCell>

                        {/* Submitted date */}
                        <GlassTableCell className="hidden sm:table-cell text-text-tertiary">
                          {formatDate(request.createdAt)}
                        </GlassTableCell>

                        {/* Actions */}
                        <GlassTableCell>
                          <div
                            className="flex items-center gap-1"
                            onClick={(e) => {
                              e.stopPropagation();
                            }}
                          >
                            {canAct && (
                              <>
                                <GlassButton
                                  variant="ghost"
                                  size="sm"
                                  aria-label="Convert to Work Order"
                                  disabled={converting}
                                  onClick={() => {
                                    handleConvert(request);
                                  }}
                                  title="Convert to Work Order"
                                >
                                  <GitMerge size={13} className="text-success" />
                                </GlassButton>
                                <GlassButton
                                  variant="ghost"
                                  size="sm"
                                  aria-label="Reject request"
                                  onClick={() => {
                                    setRejectTarget(request);
                                  }}
                                  title="Reject"
                                >
                                  <XCircle size={13} className="text-danger" />
                                </GlassButton>
                              </>
                            )}
                            {isConverted && (
                              <span className="flex items-center gap-1 text-xs text-success">
                                <CheckCircle2 size={13} />
                                Converted
                              </span>
                            )}
                            {isRejected && (
                              <span className="flex items-center gap-1 text-xs text-danger">
                                <AlertTriangle size={13} />
                                Rejected
                              </span>
                            )}
                          </div>
                        </GlassTableCell>
                      </GlassTableRow>

                      {/* Expanded detail panel */}
                      {isExpanded && (
                        <GlassTableRow key={`detail-${String(request.id)}`}>
                          <GlassTableCell colSpan={9} className="p-0">
                            <RequestDetailPanel request={request} />
                          </GlassTableCell>
                        </GlassTableRow>
                      )}
                    </>
                  );
                })}
              </GlassTableBody>
            </GlassTable>
          </div>
        )}
      </GlassCard>

      {/* Reject confirmation dialog */}
      <RejectDialog
        request={rejectTarget}
        open={rejectTarget !== null}
        onClose={() => {
          setRejectTarget(null);
        }}
        onRejected={() => {
          setRejectTarget(null);
        }}
      />
    </div>
  );
}
